import { z } from "zod";
import { dateOnlySchema, listQuerySchema, tagsSchema, timestampSchema, uuidSchema } from "./api.ts";
import { csvArraySchema, queryBooleanSchema, timestampsShape, userRefSchema } from "./common.ts";
import { portalRoleSchema } from "./portal.ts";
import { taskPrioritySchema } from "./tasks.ts";

/**
 * TEMPLATES — roadmap item 15: onboarding checklists and industry task packs
 * (PRD §9 US-15.1 … US-15.7).
 *
 * VOCABULARY
 *  · A **pack** is what the library lists: "Real estate — project launch
 *    campaign". It is either CURATED (`isSystem`, shipped by Lean2Automate,
 *    read-only, cloned to customise) or the workspace's own.
 *  · A pack's **content** is one or more project templates (milestones and
 *    tasks with RELATIVE offsets in working days, role placeholders, deliverable
 *    types, estimates, dependencies, recurring rules, retainer quotas) plus
 *    onboarding checklists (internal and client-facing items).
 *  · Editing changes the pack's WORKING COPY. **Publishing** freezes it as an
 *    immutable **version**; a project records the version it was built from, so
 *    editing a pack never rewrites a project that already exists (US-15.6).
 *  · Applying a pack starts an **onboarding run** — a persisted saga per client:
 *    STARTED → CHECKLIST_CREATED → PROJECT_BUILT → BILLING_SCHEDULED →
 *    PORTAL_INVITED → COMPLETED, stopping at NEEDS_ATTENTION with Retry / Skip.
 *
 * Offsets are WORKING days: day 0 is the (first working day on or after the)
 * start date, and weekends and workspace holidays are stepped over.
 */

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/** A stable identifier inside a pack: `kickoff_call`, `rera-check`. */
export const TEMPLATE_KEY_REGEX = /^[a-z0-9][a-z0-9_-]{0,63}$/;
export const templateKeySchema = z.string().regex(TEMPLATE_KEY_REGEX, "Use lowercase letters, digits, - or _ (max 64).");

/**
 * A role placeholder: `designer`, or `{{designer}}` as authors type it. The API
 * stores and returns the bare key; the braces are accepted on input only.
 */
export const TEMPLATE_ROLE_REGEX = /^(\{\{\s*)?[a-z][a-z0-9_]{1,39}(\s*\}\})?$/;
export const templateRoleSchema = z.string().regex(TEMPLATE_ROLE_REGEX, "Use a role key such as designer or {{designer}}.");

/** The roles curated packs use, with the member titles that resolve to them. */
export const TEMPLATE_ROLES = [
  "account_manager",
  "project_manager",
  "strategist",
  "designer",
  "copywriter",
  "social_media_manager",
  "performance_marketer",
  "seo_specialist",
  "video_editor",
  "photographer",
  "developer",
  "influencer_manager",
  "ecommerce_manager",
] as const;
export type TemplateRole = (typeof TEMPLATE_ROLES)[number];

export const TEMPLATE_ROLE_LABELS: Record<TemplateRole, string> = {
  account_manager: "Account manager",
  project_manager: "Project manager",
  strategist: "Strategist",
  designer: "Designer",
  copywriter: "Copywriter",
  social_media_manager: "Social media manager",
  performance_marketer: "Performance marketer",
  seo_specialist: "SEO specialist",
  video_editor: "Video editor",
  photographer: "Photographer",
  developer: "Developer",
  influencer_manager: "Influencer manager",
  ecommerce_manager: "E-commerce manager",
};

/**
 * What an agency sells, as a canonical kind. Catalog services are free-form per
 * workspace, so the API classifies each one (code, tags, category, name) into
 * these kinds; curated packs and conditional tasks key off the kinds.
 */
export const SERVICE_KINDS = [
  "social_media",
  "performance_marketing",
  "seo",
  "website",
  "branding",
  "content",
  "video",
  "photography",
  "influencer",
  "marketplace",
  "reputation",
  "crm_messaging",
] as const;
export type ServiceKind = (typeof SERVICE_KINDS)[number];
export const serviceKindSchema = z.enum(SERVICE_KINDS);

export const SERVICE_KIND_LABELS: Record<ServiceKind, string> = {
  social_media: "Social media management",
  performance_marketing: "Performance marketing",
  seo: "SEO",
  website: "Website design & development",
  branding: "Brand identity",
  content: "Content writing",
  video: "Video production",
  photography: "Photography",
  influencer: "Influencer marketing",
  marketplace: "Marketplace & delivery-app listings",
  reputation: "Reviews & Google Business Profile",
  crm_messaging: "WhatsApp, email & CRM",
};

export const TEMPLATE_PACK_KINDS = ["onboarding", "delivery", "campaign"] as const;
export type TemplatePackKind = (typeof TEMPLATE_PACK_KINDS)[number];
export const templatePackKindSchema = z.enum(TEMPLATE_PACK_KINDS);

export const TEMPLATE_PROJECT_TYPES = ["retainer", "one_off"] as const;
export const templateProjectTypeSchema = z.enum(TEMPLATE_PROJECT_TYPES);

export const CHECKLIST_AUDIENCES = ["internal", "client"] as const;
export type ChecklistAudience = (typeof CHECKLIST_AUDIENCES)[number];
export const checklistAudienceSchema = z.enum(CHECKLIST_AUDIENCES);

/**
 * `access` = grant us access (preferred); `credential` = only when a grant is
 * impossible — the value goes into an encrypted vault field (US-15.2).
 */
export const CHECKLIST_ITEM_KINDS = ["task", "document", "access", "credential", "approval", "form", "meeting", "payment"] as const;
export type ChecklistItemKind = (typeof CHECKLIST_ITEM_KINDS)[number];
export const checklistItemKindSchema = z.enum(CHECKLIST_ITEM_KINDS);

export const TEMPLATE_RECURRENCES = ["weekly", "monthly"] as const;
export const templateRecurrenceSchema = z.enum(TEMPLATE_RECURRENCES);

const deliverableTypeSchema = z.string().trim().min(1).max(60);

// ---------------------------------------------------------------------------
// Pack content — the response shapes (every key present) …
// ---------------------------------------------------------------------------

export const templateMilestoneSchema = z.object({
  key: templateKeySchema,
  name: z.string().min(1).max(200),
  /** Working days from the project start to the milestone date. */
  offsetDays: z.number().int().min(0).max(400),
  note: z.string().max(2000).nullable(),
  requiresServices: z.array(serviceKindSchema).max(12),
});
export type TemplateMilestone = z.infer<typeof templateMilestoneSchema>;

export const templateTaskSchema = z.object({
  key: templateKeySchema,
  /** May use `{client_name}`, `{project_name}`, `{start_date}`, `{launch_date}` and any variable the run supplies. */
  title: z.string().min(1).max(300),
  description: z.string().max(5000).nullable(),
  milestoneKey: templateKeySchema.nullable(),
  /** Working days from the project start to the day work begins. */
  startOffsetDays: z.number().int().min(0).max(400),
  /** Working days of work; due = start + duration (0 = due the day it starts). */
  durationDays: z.number().int().min(0).max(120),
  role: templateRoleSchema.nullable(),
  estimateMinutes: z.number().int().min(0).max(100000).nullable(),
  priority: taskPrioritySchema,
  /** Counts against the retainer quota when done (US-3.5). */
  deliverableType: deliverableTypeSchema.nullable(),
  /** Keys of tasks in the same project template that must finish first. */
  dependsOn: z.array(templateKeySchema).max(20),
  checklist: z.array(z.string().min(1).max(500)).max(50),
  tags: tagsSchema,
  /** Conditional block (US-15.5): included only when ANY of these services was won. Empty = always. */
  requiresServices: z.array(serviceKindSchema).max(12),
});
export type TemplateTask = z.infer<typeof templateTaskSchema>;

export const templateRecurringSchema = z.object({
  key: templateKeySchema,
  title: z.string().min(1).max(300),
  description: z.string().max(5000).nullable(),
  role: templateRoleSchema.nullable(),
  frequency: templateRecurrenceSchema,
  /** Weekly rules: 0 = Sunday … 6 = Saturday. */
  weekday: z.number().int().min(0).max(6).nullable(),
  /** Monthly rules: a day of the month, or `lastWorkingDay`. */
  dayOfMonth: z.number().int().min(1).max(31).nullable(),
  lastWorkingDay: z.boolean(),
  /** Calendar days between the instance firing and it being due. */
  dueOffsetDays: z.number().int().min(0).max(30),
  estimateMinutes: z.number().int().min(0).max(100000).nullable(),
  priority: taskPrioritySchema,
  deliverableType: deliverableTypeSchema.nullable(),
  checklist: z.array(z.string().min(1).max(500)).max(50),
  requiresServices: z.array(serviceKindSchema).max(12),
});
export type TemplateRecurring = z.infer<typeof templateRecurringSchema>;

/** A retainer commitment the pack proposes for the engagement (US-3.5) — scheduled by the BILLING step. */
export const templateQuotaSchema = z.object({
  deliverableType: deliverableTypeSchema,
  qtyPerMonth: z.number().int().min(1).max(1000),
  rollover: z.enum(["none", "carry_forward"]),
  requiresServices: z.array(serviceKindSchema).max(12),
});
export type TemplateQuota = z.infer<typeof templateQuotaSchema>;

export const projectTemplateSchema = z.object({
  key: templateKeySchema,
  name: z.string().min(1).max(200),
  description: z.string().max(5000).nullable(),
  projectType: templateProjectTypeSchema,
  milestones: z.array(templateMilestoneSchema).max(40),
  tasks: z.array(templateTaskSchema).max(300),
  recurring: z.array(templateRecurringSchema).max(20),
  quotas: z.array(templateQuotaSchema).max(20),
});
export type ProjectTemplate = z.infer<typeof projectTemplateSchema>;

export const onboardingChecklistItemSchema = z.object({
  key: templateKeySchema,
  title: z.string().min(1).max(300),
  description: z.string().max(5000).nullable(),
  audience: checklistAudienceSchema,
  kind: checklistItemKindSchema,
  ownerRole: templateRoleSchema.nullable(),
  /** Working days from the run's start date. */
  dueOffsetDays: z.number().int().min(0).max(120),
  /** "GST certificate", "Logo (vector)", … — what the item is waiting for. */
  requiredDocuments: z.array(z.string().min(1).max(200)).max(15),
  requiresServices: z.array(serviceKindSchema).max(12),
});
export type OnboardingChecklistItem = z.infer<typeof onboardingChecklistItemSchema>;

export const onboardingChecklistSchema = z.object({
  key: templateKeySchema,
  name: z.string().min(1).max(200),
  description: z.string().max(5000).nullable(),
  items: z.array(onboardingChecklistItemSchema).max(80),
});
export type OnboardingChecklist = z.infer<typeof onboardingChecklistSchema>;

export const templatePackContentSchema = z.object({
  projectTemplates: z.array(projectTemplateSchema).max(3),
  checklists: z.array(onboardingChecklistSchema).max(3),
});
export type TemplatePackContent = z.infer<typeof templatePackContentSchema>;

// ---------------------------------------------------------------------------
// … and the input shapes (optional keys; the API fills the defaults)
// ---------------------------------------------------------------------------

const milestoneInputSchema = z
  .object({
    key: templateKeySchema,
    name: z.string().trim().min(1).max(200),
    offsetDays: z.number().int().min(0).max(400),
    note: z.string().max(2000).nullable().optional(),
    requiresServices: z.array(serviceKindSchema).max(12).optional(),
  })
  .strict();

const taskInputSchema = z
  .object({
    key: templateKeySchema,
    title: z.string().trim().min(1).max(300),
    description: z.string().max(5000).nullable().optional(),
    milestoneKey: templateKeySchema.nullable().optional(),
    startOffsetDays: z.number().int().min(0).max(400),
    durationDays: z.number().int().min(0).max(120).optional(),
    role: templateRoleSchema.nullable().optional(),
    estimateMinutes: z.number().int().min(0).max(100000).nullable().optional(),
    priority: taskPrioritySchema.optional(),
    deliverableType: deliverableTypeSchema.nullable().optional(),
    dependsOn: z.array(templateKeySchema).max(20).optional(),
    checklist: z.array(z.string().trim().min(1).max(500)).max(50).optional(),
    tags: tagsSchema.optional(),
    requiresServices: z.array(serviceKindSchema).max(12).optional(),
  })
  .strict();

const recurringInputSchema = z
  .object({
    key: templateKeySchema,
    title: z.string().trim().min(1).max(300),
    description: z.string().max(5000).nullable().optional(),
    role: templateRoleSchema.nullable().optional(),
    frequency: templateRecurrenceSchema,
    weekday: z.number().int().min(0).max(6).nullable().optional(),
    dayOfMonth: z.number().int().min(1).max(31).nullable().optional(),
    lastWorkingDay: z.boolean().optional(),
    dueOffsetDays: z.number().int().min(0).max(30).optional(),
    estimateMinutes: z.number().int().min(0).max(100000).nullable().optional(),
    priority: taskPrioritySchema.optional(),
    deliverableType: deliverableTypeSchema.nullable().optional(),
    checklist: z.array(z.string().trim().min(1).max(500)).max(50).optional(),
    requiresServices: z.array(serviceKindSchema).max(12).optional(),
  })
  .strict();

const quotaInputSchema = z
  .object({
    deliverableType: deliverableTypeSchema,
    qtyPerMonth: z.number().int().min(1).max(1000),
    rollover: z.enum(["none", "carry_forward"]).optional(),
    requiresServices: z.array(serviceKindSchema).max(12).optional(),
  })
  .strict();

const projectTemplateInputSchema = z
  .object({
    key: templateKeySchema,
    name: z.string().trim().min(1).max(200),
    description: z.string().max(5000).nullable().optional(),
    projectType: templateProjectTypeSchema.optional(),
    milestones: z.array(milestoneInputSchema).max(40).optional(),
    tasks: z.array(taskInputSchema).max(300).optional(),
    recurring: z.array(recurringInputSchema).max(20).optional(),
    quotas: z.array(quotaInputSchema).max(20).optional(),
  })
  .strict();

const checklistItemInputSchema = z
  .object({
    key: templateKeySchema,
    title: z.string().trim().min(1).max(300),
    description: z.string().max(5000).nullable().optional(),
    audience: checklistAudienceSchema,
    kind: checklistItemKindSchema.optional(),
    ownerRole: templateRoleSchema.nullable().optional(),
    dueOffsetDays: z.number().int().min(0).max(120).optional(),
    requiredDocuments: z.array(z.string().trim().min(1).max(200)).max(15).optional(),
    requiresServices: z.array(serviceKindSchema).max(12).optional(),
  })
  .strict();

const checklistInputSchema = z
  .object({
    key: templateKeySchema,
    name: z.string().trim().min(1).max(200),
    description: z.string().max(5000).nullable().optional(),
    items: z.array(checklistItemInputSchema).max(80).optional(),
  })
  .strict();

/** The whole working copy as an editor sends it. Replaced as a whole. */
export const templatePackContentInputSchema = z
  .object({
    projectTemplates: z.array(projectTemplateInputSchema).max(3).optional(),
    checklists: z.array(checklistInputSchema).max(3).optional(),
  })
  .strict();
export type TemplatePackContentInput = z.input<typeof templatePackContentInputSchema>;

// ---------------------------------------------------------------------------
// Content lint — pure, shared, so the editor can show the same errors the API returns
// ---------------------------------------------------------------------------

export interface TemplateLintIssue {
  path: (string | number)[];
  message: string;
}

/**
 * The rules a schema cannot express: keys unique, references resolve,
 * dependencies acyclic and pointing backwards-or-level in time. Returns every
 * problem, each with the path the editor highlights.
 */
export function lintTemplatePackContent(content: TemplatePackContent): TemplateLintIssue[] {
  const issues: TemplateLintIssue[] = [];
  const projectKeys = new Set<string>();
  content.projectTemplates.forEach((project, p) => {
    const base: (string | number)[] = ["projectTemplates", p];
    if (projectKeys.has(project.key)) issues.push({ path: [...base, "key"], message: `The project template key "${project.key}" is used twice.` });
    projectKeys.add(project.key);

    const milestoneKeys = new Set<string>();
    project.milestones.forEach((milestone, m) => {
      if (milestoneKeys.has(milestone.key)) issues.push({ path: [...base, "milestones", m, "key"], message: `The milestone key "${milestone.key}" is used twice.` });
      milestoneKeys.add(milestone.key);
    });

    const taskKeys = new Map<string, number>();
    project.tasks.forEach((task, i) => {
      if (taskKeys.has(task.key)) issues.push({ path: [...base, "tasks", i, "key"], message: `The task key "${task.key}" is used twice.` });
      taskKeys.set(task.key, i);
    });
    project.tasks.forEach((task, i) => {
      if (task.milestoneKey && !milestoneKeys.has(task.milestoneKey)) {
        issues.push({ path: [...base, "tasks", i, "milestoneKey"], message: `"${task.title}" points at a milestone that does not exist.` });
      }
      for (const dep of task.dependsOn) {
        if (dep === task.key) issues.push({ path: [...base, "tasks", i, "dependsOn"], message: `"${task.title}" cannot wait for itself.` });
        else if (!taskKeys.has(dep)) issues.push({ path: [...base, "tasks", i, "dependsOn"], message: `"${task.title}" waits for "${dep}", which is not a task in this template.` });
      }
    });

    // Cycle check: depth-first over the dependency graph.
    const byKey = new Map(project.tasks.map((task) => [task.key, task]));
    const state = new Map<string, 1 | 2>();
    const visit = (key: string, trail: string[]): boolean => {
      const mark = state.get(key);
      if (mark === 2) return false;
      if (mark === 1) {
        const index = taskKeys.get(key) ?? 0;
        issues.push({ path: [...base, "tasks", index, "dependsOn"], message: `Circular dependency: ${[...trail, key].join(" → ")}.` });
        return true;
      }
      state.set(key, 1);
      for (const dep of byKey.get(key)?.dependsOn ?? []) if (byKey.has(dep) && visit(dep, [...trail, key])) return true;
      state.set(key, 2);
      return false;
    };
    for (const task of project.tasks) if (!state.has(task.key) && visit(task.key, [])) break;

    const recurringKeys = new Set<string>();
    project.recurring.forEach((rule, r) => {
      if (recurringKeys.has(rule.key)) issues.push({ path: [...base, "recurring", r, "key"], message: `The recurring key "${rule.key}" is used twice.` });
      recurringKeys.add(rule.key);
      if (rule.frequency === "weekly" && rule.weekday === null) issues.push({ path: [...base, "recurring", r, "weekday"], message: "A weekly rule needs a weekday." });
      if (rule.frequency === "monthly" && rule.dayOfMonth === null && !rule.lastWorkingDay) {
        issues.push({ path: [...base, "recurring", r, "dayOfMonth"], message: "A monthly rule needs a day of the month or the last working day." });
      }
    });

    const quotaTypes = new Set<string>();
    project.quotas.forEach((quota, q) => {
      const type = quota.deliverableType.toLowerCase();
      if (quotaTypes.has(type)) issues.push({ path: [...base, "quotas", q, "deliverableType"], message: `"${quota.deliverableType}" is committed twice.` });
      quotaTypes.add(type);
    });
  });

  const checklistKeys = new Set<string>();
  const itemKeys = new Set<string>();
  content.checklists.forEach((checklist, c) => {
    if (checklistKeys.has(checklist.key)) issues.push({ path: ["checklists", c, "key"], message: `The checklist key "${checklist.key}" is used twice.` });
    checklistKeys.add(checklist.key);
    checklist.items.forEach((item, i) => {
      // Item keys are unique across the pack: a run's items are keyed by them.
      if (itemKeys.has(item.key)) issues.push({ path: ["checklists", c, "items", i, "key"], message: `The checklist item key "${item.key}" is used twice.` });
      itemKeys.add(item.key);
    });
  });
  return issues;
}

/** Strips `{{ }}` from a role placeholder: `{{designer}}` → `designer`. */
export function normalizeTemplateRole(role: string | null | undefined): string | null {
  if (!role) return null;
  return role.replace(/^\{\{\s*/, "").replace(/\s*\}\}$/, "").trim() || null;
}

/** Fills the input shape's defaults, producing the canonical stored content. */
export function normalizeTemplatePackContent(input: z.output<typeof templatePackContentInputSchema>): TemplatePackContent {
  return {
    projectTemplates: (input.projectTemplates ?? []).map((project) => ({
      key: project.key,
      name: project.name,
      description: project.description ?? null,
      projectType: project.projectType ?? "one_off",
      milestones: (project.milestones ?? []).map((m) => ({ key: m.key, name: m.name, offsetDays: m.offsetDays, note: m.note ?? null, requiresServices: [...(m.requiresServices ?? [])] })),
      tasks: (project.tasks ?? []).map((task) => ({
        key: task.key,
        title: task.title,
        description: task.description ?? null,
        milestoneKey: task.milestoneKey ?? null,
        startOffsetDays: task.startOffsetDays,
        durationDays: task.durationDays ?? 0,
        role: normalizeTemplateRole(task.role),
        estimateMinutes: task.estimateMinutes ?? null,
        priority: task.priority ?? "normal",
        deliverableType: task.deliverableType ?? null,
        dependsOn: [...new Set(task.dependsOn ?? [])],
        checklist: [...(task.checklist ?? [])],
        tags: [...(task.tags ?? [])],
        requiresServices: [...(task.requiresServices ?? [])],
      })),
      recurring: (project.recurring ?? []).map((rule) => ({
        key: rule.key,
        title: rule.title,
        description: rule.description ?? null,
        role: normalizeTemplateRole(rule.role),
        frequency: rule.frequency,
        weekday: rule.weekday ?? null,
        dayOfMonth: rule.dayOfMonth ?? null,
        lastWorkingDay: rule.lastWorkingDay ?? false,
        dueOffsetDays: rule.dueOffsetDays ?? 0,
        estimateMinutes: rule.estimateMinutes ?? null,
        priority: rule.priority ?? "normal",
        deliverableType: rule.deliverableType ?? null,
        checklist: [...(rule.checklist ?? [])],
        requiresServices: [...(rule.requiresServices ?? [])],
      })),
      quotas: (project.quotas ?? []).map((quota) => ({
        deliverableType: quota.deliverableType,
        qtyPerMonth: quota.qtyPerMonth,
        rollover: quota.rollover ?? "none",
        requiresServices: [...(quota.requiresServices ?? [])],
      })),
    })),
    checklists: (input.checklists ?? []).map((checklist) => ({
      key: checklist.key,
      name: checklist.name,
      description: checklist.description ?? null,
      items: (checklist.items ?? []).map((item) => ({
        key: item.key,
        title: item.title,
        description: item.description ?? null,
        audience: item.audience,
        kind: item.kind ?? "task",
        ownerRole: normalizeTemplateRole(item.ownerRole),
        dueOffsetDays: item.dueOffsetDays ?? 0,
        requiredDocuments: [...(item.requiredDocuments ?? [])],
        requiresServices: [...(item.requiresServices ?? [])],
      })),
    })),
  };
}

// ---------------------------------------------------------------------------
// Packs
// ---------------------------------------------------------------------------

export const templatePackRefSchema = z.object({ id: uuidSchema, key: z.string(), name: z.string(), version: z.number().int().nullable() });
export type TemplatePackRef = z.infer<typeof templatePackRefSchema>;

/** One pack as the library lists it (US-15.1). Numbers describe the CURRENT published version (the draft when none). */
export const templatePackSchema = z.object({
  id: uuidSchema,
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  kind: templatePackKindSchema,
  /** Canonical top-level industry codes, e.g. `REAL_ESTATE`. Empty = suits any industry. */
  industryCodes: z.array(z.string()),
  /** Canonical sub-industry codes, e.g. `REAL_ESTATE.RESIDENTIAL_DEVELOPER`. */
  subIndustryCodes: z.array(z.string()),
  serviceKinds: z.array(serviceKindSchema),
  isSystem: z.boolean(),
  published: z.boolean(),
  /** 0 = never published (a draft). */
  currentVersion: z.number().int(),
  /** The working copy differs from the current version — publish to make it applicable. */
  hasDraftChanges: z.boolean(),
  source: templatePackRefSchema.nullable(),
  taskCount: z.number().int(),
  milestoneCount: z.number().int(),
  checklistItemCount: z.number().int(),
  /** Working days from start to the last due date. */
  durationDays: z.number().int(),
  estimateMinutes: z.number().int(),
  roles: z.array(z.string()),
  deliverableTypes: z.array(z.string()),
  tags: z.array(z.string()),
  timesApplied: z.number().int(),
  createdBy: userRefSchema.nullable(),
  archivedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type TemplatePackDto = z.infer<typeof templatePackSchema>;

export const templatePackVersionSummarySchema = z.object({
  id: uuidSchema,
  version: z.number().int(),
  changelog: z.string().nullable(),
  contentHash: z.string(),
  taskCount: z.number().int(),
  publishedBy: userRefSchema.nullable(),
  publishedAt: timestampSchema,
});
export type TemplatePackVersionSummaryDto = z.infer<typeof templatePackVersionSummarySchema>;

/** GET /api/v1/template-packs/:id (perm `projects:read`) → the pack with its working copy and version list. */
export const templatePackDetailSchema = templatePackSchema.extend({
  content: templatePackContentSchema,
  versions: z.array(templatePackVersionSummarySchema),
});
export type TemplatePackDetailDto = z.infer<typeof templatePackDetailSchema>;

/** GET /api/v1/template-packs/:id/versions/:version (perm `projects:read`) → one immutable version. */
export const templatePackVersionSchema = templatePackVersionSummarySchema.extend({
  packId: uuidSchema,
  content: templatePackContentSchema,
});
export type TemplatePackVersionDto = z.infer<typeof templatePackVersionSchema>;

export const templatePackVersionParamsSchema = z.object({ id: uuidSchema, version: z.coerce.number().int().min(1) });

/** GET /api/v1/template-packs (perm `projects:read`) → Page<TemplatePack>. Filters AND together. */
export const listTemplatePacksQuerySchema = listQuerySchema.extend({
  industryCode: z.string().trim().max(80).optional(),
  subIndustryCode: z.string().trim().max(120).optional(),
  serviceKind: serviceKindSchema.optional(),
  kind: templatePackKindSchema.optional(),
  source: z.enum(["system", "org"]).optional(),
  published: queryBooleanSchema.optional(),
  includeArchived: queryBooleanSchema.optional(),
});
export type ListTemplatePacksQuery = z.infer<typeof listTemplatePacksQuerySchema>;

const packMetaShape = {
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(5000).nullable(),
  kind: templatePackKindSchema,
  industryCodes: z.array(z.string().trim().min(1).max(80)).max(20),
  subIndustryCodes: z.array(z.string().trim().min(1).max(120)).max(40),
  serviceKinds: z.array(serviceKindSchema).max(12),
  tags: tagsSchema,
};

/** POST /api/v1/template-packs (perm `templates:manage`) → 201 TemplatePackDetail, a draft (version 0). */
export const createTemplatePackInputSchema = z
  .object({
    name: packMetaShape.name,
    description: packMetaShape.description.optional(),
    kind: packMetaShape.kind.optional(),
    industryCodes: packMetaShape.industryCodes.optional(),
    subIndustryCodes: packMetaShape.subIndustryCodes.optional(),
    serviceKinds: packMetaShape.serviceKinds.optional(),
    tags: packMetaShape.tags.optional(),
    content: templatePackContentInputSchema.optional(),
  })
  .strict();
export type CreateTemplatePackInput = z.input<typeof createTemplatePackInputSchema>;
export type CreateTemplatePackData = z.output<typeof createTemplatePackInputSchema>;

/** PATCH /api/v1/template-packs/:id (perm `templates:manage`) → TemplatePackDetail. Merge patch. Curated packs → 409. */
export const updateTemplatePackInputSchema = z.object({ ...packMetaShape, published: z.boolean() }).partial().strict();
export type UpdateTemplatePackInput = z.input<typeof updateTemplatePackInputSchema>;

/** PUT /api/v1/template-packs/:id/content (perm `templates:manage`) → TemplatePackDetail. Replaces the working copy as a whole; lint errors → 422. */
export const replaceTemplateContentInputSchema = z.object({ content: templatePackContentInputSchema }).strict();
export type ReplaceTemplateContentInput = z.input<typeof replaceTemplateContentInputSchema>;

/** POST /api/v1/template-packs/:id/publish (perm `templates:manage`) → 201 TemplatePackVersion. Nothing changed → 409. */
export const publishTemplatePackInputSchema = z.object({ changelog: z.string().trim().min(1).max(2000) }).strict();
export type PublishTemplatePackInput = z.input<typeof publishTemplatePackInputSchema>;

/** POST /api/v1/template-packs/:id/clone (perm `templates:manage`) → 201 TemplatePackDetail, published as version 1. */
export const cloneTemplatePackInputSchema = z.object({ name: z.string().trim().min(1).max(200).optional() }).strict();
export type CloneTemplatePackInput = z.input<typeof cloneTemplatePackInputSchema>;

/**
 * POST /api/v1/template-packs/from-project (perm `templates:manage`) → 201 TemplatePackDetail.
 * "Save project as template" (US-15.5): dates become working-day offsets, people become roles.
 */
export const saveProjectAsTemplateInputSchema = z
  .object({
    projectId: uuidSchema,
    name: z.string().trim().min(1).max(200),
    description: z.string().trim().max(5000).nullable().optional(),
    industryCodes: packMetaShape.industryCodes.optional(),
    subIndustryCodes: packMetaShape.subIndustryCodes.optional(),
    serviceKinds: packMetaShape.serviceKinds.optional(),
    /** userId → role key. People not listed become a role from their project role, or `team_member`. */
    roleMap: z.record(uuidSchema, templateRoleSchema).optional(),
    /** Publish immediately as version 1 (default true). */
    publish: z.boolean().optional(),
  })
  .strict();
export type SaveProjectAsTemplateInput = z.input<typeof saveProjectAsTemplateInputSchema>;

// ---------------------------------------------------------------------------
// Recommendations (US-15.3)
// ---------------------------------------------------------------------------

/**
 * GET /api/v1/template-packs/recommendations (perm `projects:read`).
 * Industry comes from the client's company (or the lead's), unless given; services from the lead(s) won, unless given.
 */
export const recommendTemplatePacksQuerySchema = z.object({
  clientId: uuidSchema.optional(),
  leadId: uuidSchema.optional(),
  industryId: uuidSchema.optional(),
  subIndustryId: uuidSchema.optional(),
  serviceIds: csvArraySchema(uuidSchema, 50).optional(),
  kind: templatePackKindSchema.optional(),
  limit: z.coerce.number().int().min(1).max(20).optional(),
});
export type RecommendTemplatePacksQuery = z.infer<typeof recommendTemplatePacksQuerySchema>;

export const templatePackRecommendationSchema = z.object({
  pack: templatePackSchema,
  score: z.number().int(),
  /** Plain sentences, strongest first: "Made for residential developers", "Covers the SEO retainer you won". */
  reasons: z.array(z.string()),
  matchedIndustry: z.boolean(),
  matchedSubIndustry: z.boolean(),
  matchedServices: z.array(serviceKindSchema),
});
export type TemplatePackRecommendationDto = z.infer<typeof templatePackRecommendationSchema>;

export const templateRecommendationsSchema = z.object({
  industryCode: z.string().nullable(),
  subIndustryCode: z.string().nullable(),
  serviceKinds: z.array(serviceKindSchema),
  items: z.array(templatePackRecommendationSchema),
});
export type TemplateRecommendationsDto = z.infer<typeof templateRecommendationsSchema>;

// ---------------------------------------------------------------------------
// Preview and apply
// ---------------------------------------------------------------------------

const applyShape = {
  clientId: uuidSchema.optional(),
  /** Apply into an EXISTING project (US-15.4); tasks already created from this pack are not duplicated. */
  projectId: uuidSchema.optional(),
  engagementId: uuidSchema.optional(),
  /** Day 0. Defaults to today in the workspace; a non-working day moves to the next working one. */
  startDate: dateOnlySchema.optional(),
  /** Which project template of the pack to build (default: the first). */
  projectTemplateKey: templateKeySchema.optional(),
  projectName: z.string().trim().min(1).max(200).optional(),
  pmId: uuidSchema.optional(),
  /** role key → member id (or null to leave that role unassigned on purpose). */
  roleAssignments: z.record(z.string().regex(TEMPLATE_ROLE_REGEX), uuidSchema.nullable()).optional(),
  /** Services won — catalog ids, classified into kinds; or kinds directly. */
  serviceIds: z.array(uuidSchema).max(50).optional(),
  serviceKinds: z.array(serviceKindSchema).max(12).optional(),
  /** `{launch_date}`, `{campaign}` … substituted into titles and descriptions. */
  variables: z.record(z.string().regex(/^[a-z][a-z0-9_]{0,39}$/), z.string().max(200)).optional(),
};

/** POST /api/v1/template-packs/:id/preview (perm `projects:read`) → TemplatePreview. Writes nothing. `version: 0` previews the draft. */
export const previewTemplatePackInputSchema = z
  .object({ ...applyShape, version: z.number().int().min(0).optional() })
  .strict();
export type PreviewTemplatePackInput = z.input<typeof previewTemplatePackInputSchema>;
export type PreviewTemplatePackData = z.output<typeof previewTemplatePackInputSchema>;

export const ONBOARDING_STEPS = ["create_checklist", "build_project", "schedule_billing", "invite_portal"] as const;
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];
export const onboardingStepSchema = z.enum(ONBOARDING_STEPS);

export const ONBOARDING_STEP_LABELS: Record<OnboardingStep, string> = {
  create_checklist: "Create the onboarding checklist",
  build_project: "Build the project and tasks",
  schedule_billing: "Schedule billing and retainer quotas",
  invite_portal: "Invite the client to the portal",
};

/**
 * POST /api/v1/template-packs/:id/apply (perm `projects:write`) → 201 OnboardingRun (200 when `requestKey`
 * — or the same pack, client/project and start date — already started one: a double submit builds ONE project).
 */
export const applyTemplatePackInputSchema = z
  .object({
    ...applyShape,
    /** Pin an older published version; defaults to the current one. */
    version: z.number().int().min(1).optional(),
    steps: z
      .object({
        checklist: z.boolean().optional(),
        project: z.boolean().optional(),
        billing: z.boolean().optional(),
        /** Client-facing, so OFF unless asked for (R16). */
        portal: z.boolean().optional(),
      })
      .strict()
      .optional(),
    portalContactId: uuidSchema.optional(),
    portalRole: portalRoleSchema.optional(),
    /** Caller-chosen dedupe key: the same key returns the same run. */
    requestKey: z.string().trim().min(1).max(200).optional(),
  })
  .strict();
export type ApplyTemplatePackInput = z.input<typeof applyTemplatePackInputSchema>;
export type ApplyTemplatePackData = z.output<typeof applyTemplatePackInputSchema>;

export const ROLE_RESOLUTIONS = ["assignment", "account_manager", "project_manager", "project_member", "title"] as const;
export const roleResolutionSchema = z.enum(ROLE_RESOLUTIONS);

export const templateRoleResolutionSchema = z.object({
  role: z.string(),
  label: z.string(),
  userId: uuidSchema.nullable(),
  user: userRefSchema.nullable(),
  resolvedBy: roleResolutionSchema.nullable(),
  /** Members who could fill it, for the picker — several matches are NOT auto-picked. */
  candidates: z.array(userRefSchema),
  taskCount: z.number().int(),
});
export type TemplateRoleResolutionDto = z.infer<typeof templateRoleResolutionSchema>;

export const templatePreviewTaskSchema = z.object({
  key: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  milestoneKey: z.string().nullable(),
  startDate: dateOnlySchema,
  dueDate: dateOnlySchema,
  /** Why a date moved: "Diwali", "Sunday". Null when it landed where the offset said. */
  shiftedReason: z.string().nullable(),
  role: z.string().nullable(),
  assigneeId: uuidSchema.nullable(),
  estimateMinutes: z.number().int().nullable(),
  priority: taskPrioritySchema,
  deliverableType: z.string().nullable(),
  dependsOn: z.array(z.string()),
  checklist: z.array(z.string()),
  tags: z.array(z.string()),
  included: z.boolean(),
  /** Why a conditional task is left out: "Needs Performance marketing". */
  excludedReason: z.string().nullable(),
  /** Apply-later: a task from this pack already exists on the project, so it will not be duplicated. */
  alreadyApplied: z.boolean(),
});
export type TemplatePreviewTaskDto = z.infer<typeof templatePreviewTaskSchema>;

export const templatePreviewSchema = z.object({
  pack: templatePackRefSchema,
  projectTemplate: z.object({ key: z.string(), name: z.string(), projectType: templateProjectTypeSchema }).nullable(),
  projectName: z.string().nullable(),
  requestedStartDate: dateOnlySchema,
  startDate: dateOnlySchema,
  endDate: dateOnlySchema,
  workingDays: z.array(z.number().int()),
  holidaysSkipped: z.array(z.object({ date: dateOnlySchema, name: z.string() })),
  serviceKinds: z.array(serviceKindSchema),
  roles: z.array(templateRoleResolutionSchema),
  unresolvedRoles: z.array(z.string()),
  warnings: z.array(z.string()),
  milestones: z.array(z.object({ key: z.string(), name: z.string(), dueDate: dateOnlySchema, included: z.boolean(), alreadyApplied: z.boolean() })),
  tasks: z.array(templatePreviewTaskSchema),
  recurring: z.array(
    z.object({ key: z.string(), title: z.string(), frequency: templateRecurrenceSchema, role: z.string().nullable(), assigneeId: uuidSchema.nullable(), startDate: dateOnlySchema, included: z.boolean() }),
  ),
  quotas: z.array(z.object({ deliverableType: z.string(), qtyPerMonth: z.number().int(), rollover: z.string(), included: z.boolean() })),
  checklist: z.array(
    z.object({
      key: z.string(),
      title: z.string(),
      audience: checklistAudienceSchema,
      kind: checklistItemKindSchema,
      ownerRole: z.string().nullable(),
      ownerUserId: uuidSchema.nullable(),
      dueDate: dateOnlySchema,
      requiredDocuments: z.array(z.string()),
      included: z.boolean(),
    }),
  ),
  totals: z.object({
    tasks: z.number().int(),
    excludedTasks: z.number().int(),
    alreadyApplied: z.number().int(),
    unassignedTasks: z.number().int(),
    estimateMinutes: z.number().int(),
    milestones: z.number().int(),
    checklistItems: z.number().int(),
  }),
});
export type TemplatePreviewDto = z.infer<typeof templatePreviewSchema>;

// ---------------------------------------------------------------------------
// Onboarding runs (the saga)
// ---------------------------------------------------------------------------

export const ONBOARDING_STATES = [
  "started",
  "checklist_created",
  "project_built",
  "billing_scheduled",
  "portal_invited",
  "completed",
  "needs_attention",
] as const;
export type OnboardingState = (typeof ONBOARDING_STATES)[number];
export const onboardingStateSchema = z.enum(ONBOARDING_STATES);

export const ONBOARDING_STEP_STATUSES = ["pending", "done", "skipped", "failed"] as const;
export type OnboardingStepStatus = (typeof ONBOARDING_STEP_STATUSES)[number];

export const ONBOARDING_SOURCES = ["manual", "automation", "lead_conversion", "api"] as const;
export type OnboardingSource = (typeof ONBOARDING_SOURCES)[number];

export const onboardingRunStepSchema = z.object({
  key: onboardingStepSchema,
  label: z.string(),
  status: z.enum(ONBOARDING_STEP_STATUSES),
  attempts: z.number().int(),
  /** What the step did: ids and counts, never secrets. */
  result: z.record(z.string(), z.unknown()).nullable(),
  error: z.string().nullable(),
  finishedAt: timestampSchema.nullable(),
});
export type OnboardingRunStepDto = z.infer<typeof onboardingRunStepSchema>;

export const onboardingRunSchema = z.object({
  id: uuidSchema,
  pack: templatePackRefSchema,
  packVersion: z.number().int(),
  clientId: uuidSchema.nullable(),
  clientName: z.string().nullable(),
  projectId: uuidSchema.nullable(),
  engagementId: uuidSchema.nullable(),
  state: onboardingStateSchema,
  failedStep: onboardingStepSchema.nullable(),
  lastError: z.string().nullable(),
  source: z.enum(ONBOARDING_SOURCES),
  startDate: dateOnlySchema,
  steps: z.array(onboardingRunStepSchema),
  warnings: z.array(z.string()),
  items: z.object({ total: z.number().int(), done: z.number().int(), clientFacing: z.number().int(), clientDone: z.number().int() }),
  startedBy: userRefSchema.nullable(),
  completedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type OnboardingRunDto = z.infer<typeof onboardingRunSchema>;

/** GET /api/v1/onboarding-runs (perm `projects:read`) → Page<OnboardingRun>. */
export const listOnboardingRunsQuerySchema = listQuerySchema.extend({
  clientId: uuidSchema.optional(),
  projectId: uuidSchema.optional(),
  packId: uuidSchema.optional(),
  state: onboardingStateSchema.optional(),
});
export type ListOnboardingRunsQuery = z.infer<typeof listOnboardingRunsQuerySchema>;

/** POST /api/v1/onboarding-runs/:id/skip (perm `projects:write`) → OnboardingRun. A finished step → 409. */
export const skipOnboardingStepInputSchema = z.object({ step: onboardingStepSchema }).strict();
export type SkipOnboardingStepInput = z.input<typeof skipOnboardingStepInputSchema>;

export const ONBOARDING_ITEM_STATUSES = ["open", "done", "skipped"] as const;
export type OnboardingItemStatus = (typeof ONBOARDING_ITEM_STATUSES)[number];
export const onboardingItemStatusSchema = z.enum(ONBOARDING_ITEM_STATUSES);

/** One checklist line of one client's onboarding (US-15.2). */
export const onboardingItemSchema = z.object({
  id: uuidSchema,
  runId: uuidSchema,
  clientId: uuidSchema.nullable(),
  key: z.string(),
  checklistKey: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  audience: checklistAudienceSchema,
  kind: checklistItemKindSchema,
  ownerRole: z.string().nullable(),
  owner: userRefSchema.nullable(),
  dueDate: dateOnlySchema.nullable(),
  requiredDocuments: z.array(z.string()),
  status: onboardingItemStatusSchema,
  completedAt: timestampSchema.nullable(),
  /** "Priya Shah" (staff) or "Rahul (client)". */
  completedByName: z.string().nullable(),
  response: z.string().nullable(),
  /** A vault value is stored — its plaintext is never in this payload. */
  hasSecret: z.boolean(),
  secretSetAt: timestampSchema.nullable(),
  position: z.number().int(),
  ...timestampsShape,
});
export type OnboardingItemDto = z.infer<typeof onboardingItemSchema>;

/** PATCH /api/v1/onboarding-items/:id (perm `tasks:write`) → OnboardingItem. Merge patch. */
export const updateOnboardingItemInputSchema = z
  .object({
    status: onboardingItemStatusSchema,
    ownerUserId: uuidSchema.nullable(),
    dueDate: dateOnlySchema.nullable(),
    response: z.string().trim().max(4000).nullable(),
  })
  .partial()
  .strict();
export type UpdateOnboardingItemInput = z.input<typeof updateOnboardingItemInputSchema>;

/**
 * PUT /api/v1/onboarding-items/:id/secret (perm `clients:write`, or the item's owner) → 204 — and
 * PUT /api/portal/v1/onboarding/:id/secret for the client. Stored encrypted; never returned by a read.
 */
export const setOnboardingSecretInputSchema = z.object({ value: z.string().min(1).max(4000) }).strict();
export type SetOnboardingSecretInput = z.input<typeof setOnboardingSecretInputSchema>;

/** POST /api/v1/onboarding-items/:id/secret/reveal (the item's owner only) → the plaintext, once, audited. */
export const revealedSecretSchema = z.object({ value: z.string(), revealedAt: timestampSchema });
export type RevealedSecretDto = z.infer<typeof revealedSecretSchema>;

/** A client-facing item as the portal shows it — no owner, no internal notes. */
export const portalOnboardingItemSchema = z.object({
  id: uuidSchema,
  title: z.string(),
  description: z.string().nullable(),
  kind: checklistItemKindSchema,
  dueDate: dateOnlySchema.nullable(),
  requiredDocuments: z.array(z.string()),
  status: onboardingItemStatusSchema,
  completedAt: timestampSchema.nullable(),
  response: z.string().nullable(),
  hasSecret: z.boolean(),
});
export type PortalOnboardingItemDto = z.infer<typeof portalOnboardingItemSchema>;

/** POST /api/portal/v1/onboarding/:id/complete (client portal) → PortalOnboardingItem. */
export const completePortalOnboardingItemInputSchema = z.object({ response: z.string().trim().max(4000).nullable().optional() }).strict();
export type CompletePortalOnboardingItemInput = z.input<typeof completePortalOnboardingItemInputSchema>;

// ---------------------------------------------------------------------------
// Analytics (US-15.7) and vocabulary
// ---------------------------------------------------------------------------

/** GET /api/v1/template-packs/:id/analytics (perm `reports:view`). */
export const templatePackAnalyticsSchema = z.object({
  packId: uuidSchema,
  runs: z.number().int(),
  completedRuns: z.number().int(),
  /** Days from a run's start to its checklist's last item done — null until one finishes. */
  medianOnboardingDays: z.number().nullable(),
  tasksCreated: z.number().int(),
  tasksDone: z.number().int(),
  tasksDoneOnTime: z.number().int(),
  /** Basis points: done-by-due ÷ done. Null with nothing done yet. */
  onTimeRateBps: z.number().int().nullable(),
});
export type TemplatePackAnalyticsDto = z.infer<typeof templatePackAnalyticsSchema>;

/** GET /api/v1/template-packs/vocabulary → what the editor's pickers offer. */
export const templateVocabularySchema = z.object({
  roles: z.array(z.object({ key: z.string(), label: z.string() })),
  serviceKinds: z.array(z.object({ key: serviceKindSchema, label: z.string() })),
  checklistKinds: z.array(checklistItemKindSchema),
  variables: z.array(z.object({ key: z.string(), label: z.string() })),
});
export type TemplateVocabularyDto = z.infer<typeof templateVocabularySchema>;

/** Variables every run provides; a run may add its own (`variables` on apply). */
export const TEMPLATE_BUILTIN_VARIABLES = [
  { key: "client_name", label: "Client name" },
  { key: "project_name", label: "Project name" },
  { key: "start_date", label: "Start date" },
  { key: "agency_name", label: "Your agency's name" },
  { key: "launch_date", label: "Launch date (when the run supplies one)" },
] as const;
